import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { SearchInput } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { UserAvatar } from '@/components/ui/Avatar'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useNotifications, useUnreadCount, useMarkAsRead, useMarkAllAsRead, type AppNotification } from '@/hooks/useNotifications'
import { formatRelativeTime } from '@/lib/utils'
import { Bell, Moon, Sun, Check, CheckCheck, X, User, Shield, LogOut, Sparkles, BookOpen, Target, Briefcase, Building2, UserCircle, Loader2 } from 'lucide-react'
import { RELEASE_URL, RELEASE_EXPIRES_AT } from '@/components/ReleasePopup'
import { useGlobalSearch, type GlobalSearchResult } from '@/hooks/useGlobalSearch'
import { useUserSmtpStatus } from '@/hooks/useEmailMarketing'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'

interface HeaderProps {
  title?: string
  subtitle?: string
  actions?: React.ReactNode
  
}

function NotificationIcon({ type }: { type: string }) {
  const colors: Record<string, string> = {
    lead_assigned: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
    lead_qualified: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400',
    opportunity_created: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400',
    opportunity_won: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-400',
    opportunity_lost: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400',
    proposal_submitted: 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-400',
    proposal_approved: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400',
    proposal_rejected: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400',
    follow_up_reminder: 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-400',
    system: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  }
  return <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colors[type] || colors.system}`}>
    <Bell className="h-3.5 w-3.5" />
  </div>
}

function ProfileDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', handleClick)
      document.addEventListener('keydown', handleEscape)
    }
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  function handleNavigate(path: string) {
    setOpen(false)
    navigate(path)
  }

  function handleLogout() {
    setOpen(false)
    logout()
    navigate('/login')
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full hover:ring-2 hover:ring-primary/20 transition-all p-0.5"
      >
        {user?.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={user.full_name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <UserAvatar name={user?.full_name || 'User'} size="sm" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-background border rounded-xl shadow-xl z-[100] overflow-hidden">
          {/* User info section */}
          <div className="p-4 border-b">
            <div className="flex items-center gap-3">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.full_name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <UserAvatar name={user?.full_name || 'User'} size="md" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.role?.display_name}</p>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <button
              onClick={() => handleNavigate('/profile')}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
            >
              <User className="h-4 w-4 text-muted-foreground" />
              <span>Meu Perfil</span>
            </button>
            <button
              onClick={() => handleNavigate('/profile?tab=security')}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
            >
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span>Seguranca</span>
            </button>
          </div>

          <div className="border-t py-1">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left text-red-600 dark:text-red-400"
            >
              <LogOut className="h-4 w-4" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function Header({ title, subtitle, actions}: HeaderProps) {
  const { theme, setTheme } = useUIStore()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [showNotifs, setShowNotifs] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const missingPhone = !!user && !user.phone

  // Banner SMTP/IMAP — aparece se qualquer um dos dois não estiver configurado (Fase 2, aprovada em 2026-05-15).
  const { data: smtpStatus } = useUserSmtpStatus()
  const { data: outreachConfig } = useQuery({
    queryKey: ['outreach-config'],
    queryFn: async () => {
      const { data } = await api.get<{ imap?: { imap_username?: string | null } }>('/v1/outreach/config')
      return data
    },
    enabled: !!user,
    staleTime: 60_000,
  })
  const missingSmtp = !!user && smtpStatus !== undefined && !smtpStatus.configured
  const missingImap = !!user && outreachConfig !== undefined && !outreachConfig.imap?.imap_username
  const missingEmailConfig = missingSmtp || missingImap

  const { data: notifsData } = useNotifications()
  const { data: unreadCount = 0 } = useUnreadCount()
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()

  const notifications = notifsData?.items || []

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setShowNotifs(false)
    }
    if (showNotifs) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showNotifs])

  function handleNotificationClick(n: AppNotification) {
    if (!n.is_read) markAsRead.mutate(n.id)
    if (n.link) { navigate(n.link); setShowNotifs(false) }
  }

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
      <div className="flex items-center justify-between h-16 px-6">
        {title ? (
          <div>
            <h1 className="text-xl font-semibold">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>
        ) : (
          <div />
        )}

        <div className="hidden md:flex flex-1 max-w-md mx-8">
          <GlobalSearchBox searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </div>

        <div className="flex items-center gap-2">
          {actions}

          {/* Botão Playbook de Vendas — sempre visível */}
          <a
            href="https://social.myncc.com.br/portfolio/playbook-comercial-nai.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-bold uppercase tracking-wider bg-[#005092] text-white hover:bg-[#0070c9] hover:shadow-lg hover:shadow-[#005092]/30 transition-all"
            title="Abrir Playbook Comercial NAI"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Playbook de Vendas
          </a>

          {/* Botão Novas Features — válido até a mesma data do popup de release */}
          {Date.now() < RELEASE_EXPIRES_AT.getTime() && (
            <a
              href={RELEASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-bold uppercase tracking-wider bg-[#00d4aa] text-[#0a0f1a] hover:bg-[#00e8ba] hover:shadow-lg hover:shadow-[#00d4aa]/30 transition-all"
              title="Ver novas features do CRM"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Novas Features
            </a>
          )}

          {/* Notifications */}
          <div className="relative" ref={dropdownRef}>
            <Button variant="ghost" size="icon" className="relative" onClick={() => setShowNotifs(!showNotifs)}>
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>

            {showNotifs && (
              <div className="absolute right-0 top-full mt-2 w-96 bg-background border rounded-xl shadow-xl z-[100] overflow-hidden">
                <div className="flex items-center justify-between p-3 border-b">
                  <h3 className="font-semibold text-sm">Notificacoes</h3>
                  <div className="flex items-center gap-1">
                    {unreadCount > 0 && (
                      <button className="text-xs text-primary hover:underline flex items-center gap-1" onClick={() => markAllAsRead.mutate()}>
                        <CheckCheck className="h-3 w-3" /> Marcar todas como lidas
                      </button>
                    )}
                    <button className="p-1 rounded hover:bg-muted ml-1" onClick={() => setShowNotifs(false)}><X className="h-4 w-4" /></button>
                  </div>
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-muted-foreground text-sm">Nenhuma notificacao</div>
                  ) : (
                    notifications.map(n => (
                      <div key={n.id}
                        className={`flex items-start gap-3 p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0 transition-colors ${!n.is_read ? 'bg-primary/5' : ''}`}
                        onClick={() => handleNotificationClick(n)}>
                        <NotificationIcon type={n.type} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!n.is_read ? 'font-semibold' : ''}`}>{n.title}</p>
                          {n.message && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>}
                          <p className="text-[10px] text-muted-foreground mt-1">{formatRelativeTime(n.created_at)}</p>
                        </div>
                        {!n.is_read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <Button variant="ghost" size="icon" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {/* Profile Dropdown */}
          <ProfileDropdown />
        </div>
      </div>

      {missingPhone && (
        <div className="bg-amber-500/10 border-t border-amber-500/30 text-amber-700 dark:text-amber-400 px-6 py-2 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 flex-shrink-0" />
            <span>
              <strong>Atenção:</strong> seu Celular/WhatsApp está em branco. Atualize com urgência para que outros vendedores consigam te localizar em casos de leads duplicados.
            </span>
          </div>
          <Button size="sm" onClick={() => navigate('/profile')} className="flex-shrink-0">
            Atualizar agora
          </Button>
        </div>
      )}

      {missingEmailConfig && (
        <div className="bg-blue-500/10 border-t border-blue-500/30 text-blue-700 dark:text-blue-400 px-6 py-2 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 flex-shrink-0" />
            <span>
              <strong>Atenção:</strong> {missingSmtp && missingImap ? 'seu SMTP (envio) e IMAP (sincronização com o CRM) ainda não foram configurados.' : missingSmtp ? 'seu SMTP (envio de e-mails) ainda não foi configurado.' : 'seu IMAP (sincronização com o CRM) ainda não foi configurado.'} Configure agora para enviar campanhas pelo seu e-mail e ver respostas dos leads direto nas oportunidades.
            </span>
          </div>
          <Button size="sm" onClick={() => navigate('/profile')} className="flex-shrink-0">
            Configurar agora
          </Button>
        </div>
      )}
    </header>
  )
}

function GlobalSearchBox({ searchQuery, setSearchQuery }: { searchQuery: string; setSearchQuery: (s: string) => void }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { data, isFetching } = useGlobalSearch(searchQuery)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', onClick)
      document.addEventListener('keydown', onEsc)
    }
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const groups: { key: keyof NonNullable<typeof data>; label: string; icon: typeof Target }[] = [
    { key: 'leads',         label: 'Leads',         icon: Target },
    { key: 'opportunities', label: 'Oportunidades', icon: Briefcase },
    { key: 'companies',     label: 'Empresas',      icon: Building2 },
    { key: 'contacts',      label: 'Contatos',      icon: UserCircle },
  ]

  const totalResults = data
    ? groups.reduce((sum, g) => sum + (data[g.key]?.length || 0), 0)
    : 0

  function handlePick(r: GlobalSearchResult) {
    setOpen(false)
    setSearchQuery('')
    if (r.type === 'opportunity') {
      navigate(`/opportunities/${r.id}`)
    } else if (r.type === 'lead') {
      navigate(`/leads?search=${encodeURIComponent(r.label)}`)
    } else if (r.type === 'company') {
      navigate(`/leads?search=${encodeURIComponent(r.label)}`)
    } else if (r.type === 'contact') {
      const subtitleIsEmail = r.subtitle?.includes('@')
      navigate(`/leads?search=${encodeURIComponent(subtitleIsEmail ? (r.subtitle as string) : r.label)}`)
    }
  }

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <SearchInput
        placeholder="Buscar leads, oportunidades, empresas, contatos…"
        value={searchQuery}
        onChange={e => { setSearchQuery(e.target.value); setOpen(true) }}
        onFocus={() => searchQuery.trim().length >= 2 && setOpen(true)}
        onClear={() => { setSearchQuery(''); setOpen(false) }}
        className="w-full"
      />

      {open && searchQuery.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-background border rounded-xl shadow-xl z-[100] overflow-hidden max-h-[70vh] overflow-y-auto">
          {isFetching && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground border-b">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando…
            </div>
          )}

          {!isFetching && totalResults === 0 && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Nenhum resultado para <strong>"{searchQuery}"</strong>
            </div>
          )}

          {data && groups.map(g => {
            const items = data[g.key] || []
            if (items.length === 0) return null
            const Icon = g.icon
            return (
              <div key={g.key} className="border-b last:border-0">
                <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" /> {g.label}
                </div>
                {items.map(r => (
                  <button
                    key={`${r.type}-${r.id}`}
                    onClick={() => handlePick(r)}
                    className="w-full text-left px-4 py-2 hover:bg-muted transition-colors flex items-center gap-3 border-t first:border-0 border-border/40"
                  >
                    <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{r.label}</p>
                      {r.subtitle && <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
